import { createStore } from "solid-js/store";

const BASE = `${window.location.origin}/stickers`;

export type Rarity = "common" | "rare" | "epic" | "legendary";

export type Sticker = {
  id: string;
  name: string;
  url: string;
  rarity?: Rarity;
};
export type OwnedSticker = Sticker & { src?: string; at?: number };
export type Collection = {
  slots: number;
  owned: OwnedSticker[];
  equipped: string[];
};

/** Raridades: cor, holograma (mesmos valores da Store) */
export const RARITY_STYLE = {
  common: {
    label: "Comum",
    color: "#9aa4b2",
    foil: 0,
    gradient: "none",
  },
  rare: {
    label: "Raro",
    color: "#3ddc84",
    foil: 0.6,
    gradient:
      "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.75) 45%, rgba(110,255,170,0.75) 52%, transparent 66%)",
  },
  epic: {
    label: "Épico",
    color: "#b565ff",
    foil: 0.9,
    gradient:
      "linear-gradient(115deg, transparent 25%, rgba(255,255,255,0.9) 42%, rgba(110,210,255,0.85) 50%, rgba(255,110,220,0.85) 58%, rgba(255,230,120,0.7) 64%, transparent 78%)",
  },
  legendary: {
    label: "Lendário",
    color: "#2bd4ff",
    foil: 1,
    gradient:
      "linear-gradient(115deg, transparent 22%, rgba(190,245,255,0.95) 40%, rgba(40,190,255,0.95) 50%, rgba(80,120,255,0.6) 58%, rgba(255,255,255,0.95) 64%, transparent 80%)",
  },
} as const;

const [equipped, setEquipped] = createStore<Record<string, Sticker[]>>({});
const [rarities, setRarities] = createStore<Record<string, Rarity>>({});
const asked = new Set<string>();
let queue: string[] = [];
let timer: number | undefined;
let raritiesRequested = false;

async function flush() {
  timer = undefined;
  const ids = queue.splice(0, 100);
  if (!ids.length) return;
  try {
    const r = await fetch(`${BASE}/list?ids=${ids.join(",")}`);
    const data = r.ok ? await r.json() : {};
    for (const id of ids) setEquipped(id, data[id] ?? []);
  } catch {
    for (const id of ids) asked.delete(id);
  }
  if (queue.length) timer = window.setTimeout(flush, 30);
}

/**
 * Carrega o catalogo uma unica vez para descobrir a raridade de cada sticker
 */
async function loadRarities() {
  if (raritiesRequested) return;
  raritiesRequested = true;
  try {
    const r = await fetch(`${BASE}/catalog`);
    if (!r.ok) throw new Error("catalog");
    const d = await r.json();
    const list: any[] = Array.isArray(d)
      ? d
      : (d.stickers ?? d.catalog ?? d.items ?? d.list ?? []);
    for (const s of list) {
      if (s?.id && s?.rarity) setRarities(s.id, s.rarity as Rarity);
    }
  } catch {
    raritiesRequested = false;
  }
}

/**
 * Raridade de um sticker (reativo). Undefined = sem raridade.
 */
export function rarityOf(id?: string): Rarity | undefined {
  if (!id) return undefined;
  void loadRarities();
  return rarities[id];
}

/**
 * Stickers equipados do usuario (reativo). Busca em lote na primeira leitura.
 */
export function stickersOf(userId?: string): Sticker[] {
  if (!userId) return [];
  if (!asked.has(userId)) {
    asked.add(userId);
    queue.push(userId);
    if (timer === undefined) timer = window.setTimeout(flush, 30);
  }
  return equipped[userId] ?? [];
}

/**
 * Colecao completa de um usuario (stickers que possui, slots e equipados)
 */
export async function fetchCollection(userId: string): Promise<Collection> {
  const r = await fetch(`${BASE}/owned/${userId}`, { cache: "no-store" });
  if (!r.ok) throw new Error("erro ao carregar os stickers");
  const d = await r.json();
  return {
    slots: d.slots ?? 3,
    owned: d.owned ?? [],
    equipped: d.equipped ?? [],
  };
}

/**
 * Salva os stickers equipados do proprio usuario (ordem importa)
 */
export async function saveEquipped(
  token: string,
  userId: string,
  ids: string[],
) {
  const r = await fetch(`${BASE}/equipped`, {
    method: "PUT",
    headers: { "x-session-token": token, "content-type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? "erro ao salvar os stickers");
  asked.add(userId);
  setEquipped(userId, data.equipped ?? []);
  return data;
}
